import { angleDelta, clamp, lerp } from '../core/math';
import { STATE_INTERVAL_MS, type RemoteState } from './protocol';

/** How far in the past remote players are drawn. Hides network jitter. */
export const INTERP_DELAY_MS = 150;
/** Longer gaps (an idle player that just started moving) still blend over this long. */
const MAX_BLEND_MS = STATE_INTERVAL_MS * 2.5;
/** A jump in the clock offset this big means a pause or hitch: resync right away. */
const RESYNC_MS = 1000;
/** Moving farther than this between two updates is a teleport, not a walk. */
const TELEPORT_M = 10;
const MAX_SNAPSHOTS = 40;

const lerpAngle = (a: number, b: number, t: number): number => a + angleDelta(a, b) * t;

function mix(a: RemoteState, b: RemoteState, k: number): RemoteState {
  const d = k < 0.5 ? a : b;
  const cars = a.driving && b.driving;
  return {
    ...d,
    x: lerp(a.x, b.x, k),
    y: lerp(a.y, b.y, k),
    z: lerp(a.z, b.z, k),
    facing: lerpAngle(a.facing, b.facing, k),
    speed: lerp(a.speed, b.speed, k),
    flip: lerpAngle(a.flip, b.flip, k),
    carX: cars ? lerp(a.carX, b.carX, k) : d.carX,
    carZ: cars ? lerp(a.carZ, b.carZ, k) : d.carZ,
    carHeading: cars ? lerpAngle(a.carHeading, b.carHeading, k) : d.carHeading,
  };
}

/**
 * Buffers one remote player's updates and replays them slightly in the past,
 * interpolating between snapshots. Snapshots are placed on the sender's clock, and the
 * offset to our clock is estimated from the fastest-arriving updates, so uneven
 * network delivery doesn't turn into stuttery movement.
 */
export class SnapshotBuffer {
  private readonly snaps: RemoteState[] = [];
  /** Our clock minus the sender's clock (plus the fastest observed latency). */
  private offset: number | null = null;

  push(s: RemoteState, now: number): void {
    const last = this.snaps[this.snaps.length - 1];
    if (last && s.t <= last.t) return; // duplicate or stale
    const sample = now - s.t;
    if (this.offset === null || sample < this.offset || sample - this.offset > RESYNC_MS)
      this.offset = sample;
    else this.offset += (sample - this.offset) * 0.02; // latency crept up: follow it slowly
    this.snaps.push(s);
    if (this.snaps.length > MAX_SNAPSHOTS) this.snaps.shift();
  }

  /** What to draw at our time `now`, or null before anything has arrived. */
  sample(now: number): RemoteState | null {
    const first = this.snaps[0];
    if (!first || this.offset === null) return null;
    const at = now - this.offset - INTERP_DELAY_MS;
    while (this.snaps.length > 1 && (this.snaps[1] as RemoteState).t <= at) this.snaps.shift();
    const a = this.snaps[0] as RemoteState;
    const b = this.snaps[1];
    if (!b || at <= a.t) return a;
    if (Math.hypot(b.x - a.x, b.z - a.z) > TELEPORT_M) return a;
    const span = Math.min(b.t - a.t, MAX_BLEND_MS);
    return mix(a, b, clamp((at - (b.t - span)) / span, 0, 1));
  }

  clear(): void {
    this.snaps.length = 0;
    this.offset = null;
  }
}
